/* eslint-disable @typescript-eslint/no-explicit-any */
import { TaxCouponFormValues } from '@/core/domain/coupon/coupons/coupons.types';
import { Button, Card, Checkbox, Empty, Form, Space, Typography } from 'antd';
import SelectRecipientType from '../../../InvoiceForms/InvoiceRecipientForm/RecipientForm/SelectRecipientType';
import AddressForm from '@/components/_common/AddressModal/AddressForm';
import { AddressDefaultType } from '@/components/_common/AddressModal/types';
import { useEffect, useState } from 'react';
import { RecipientType } from '@/core/domain/invoice/invoice/Invoice.types';
import { applyMask } from '@/utils/mask';
import { cepMask } from '@/utils/mask/commonMasks';
import { Group } from '@/components/_common';
import { FaCheck } from 'react-icons/fa';
import { useSupplierMutation } from '@/core/domain/people/Suppliers/Suppliers.hooks';
import { useInsurerMutation } from '@/core/domain/people/Insurers/Insurers.hooks';
import { useCustomerAddressesMutation } from '@/core/domain/people/CustomerAddresses/CustomerAddresses.hooks';

const { Title } = Typography;

interface Props {
  setAddressError: (value: string | null) => void;
}

const InvoiceCfeRecipientData: React.FC<Props> = ({ setAddressError }) => {
  const form = Form.useFormInstance<TaxCouponFormValues>();
  const formValues = Form.useWatch([], form);

  const [addressForm] = Form.useForm<AddressDefaultType>();
  const addressFormValues = Form.useWatch([], addressForm);

  const [recipientAddresses, setRecipientAddresses] = useState<
    AddressDefaultType[]
  >([]);
  const [addressId, setAddressId] = useState<number>();
  const [manualAddress, setManualAddress] = useState<boolean>(false);

  const customerAddress = useCustomerAddressesMutation();
  const supplierDetail = useSupplierMutation();
  const insurerDetail = useInsurerMutation();

  useEffect(() => {
    (async () => {
      const recipient = formValues?.recipient;

      if (
        recipient?.recipientType === RecipientType.customer &&
        recipient?.customerId
      ) {
        const addresses = await customerAddress.mutateAsync(
          recipient.customerId,
        );
        setRecipientAddresses(addresses.data || []);
      }
      if (
        recipient?.recipientType === RecipientType.supplier &&
        recipient?.supplierId
      ) {
        const result = await supplierDetail.mutateAsync(recipient?.supplierId);
        setRecipientAddresses(result?.address ? [result.address] : []);
      }
      if (
        recipient?.recipientType === RecipientType.insurance &&
        recipient?.insurerId
      ) {
        const result = await insurerDetail.mutateAsync(recipient?.insurerId);
        setRecipientAddresses(result?.addressesDTO || []);
      }
    })();
  }, [formValues?.recipient]);

  useEffect(() => {
    if (addressFormValues) {
      setAddressError(null);
      form.setFieldsValue({
        ...formValues,
        recipient: {
          ...formValues.recipient,
          deliveryAddress: {
            zipcode: addressFormValues.zipCode,
            country: 'BR',
            complement: addressFormValues.complement,
            district: addressFormValues.district,
            state: addressFormValues.state || '',
            street: addressFormValues.street,
            streetNumber: addressFormValues.number,
            city: addressFormValues.city || '',
            cityId: addressFormValues.cityId,
          },
        },
      });
    }
  }, [addressFormValues]);

  function applyAddress(address?: AddressDefaultType) {
    if (address) {
      form.setFieldValue(['recipient', 'deliveryAddress'], {
        zipcode: address.zipCode,
        district: address.district,
        state: address.state || '',
        street: address.street,
        streetNumber: address.number,
        city: address.city || '',
        cityId: address.cityId,
        complement: address.complement,
      });
      setAddressError(null);
    } else {
      form.setFieldValue(['recipient', 'deliveryAddress'], undefined);
    }
  }

  return (
    <>
      <Space size="large" direction="vertical" style={{ width: '100%' }}>
        <Title level={4} style={{ margin: 0 }}>
          Dados do destinatário
        </Title>

        <SelectRecipientType form={form as any} />

        <Form.Item
          name="hasCustomerAddress"
          initialValue={false}
          valuePropName="checked"
          noStyle
          getValueFromEvent={({ target }) => {
            setAddressError(null);
            return target.checked;
          }}
        >
          <Checkbox disabled={!formValues?.recipient?.cpfCnpj}>
            Adicionar endereço do destinatário
          </Checkbox>
        </Form.Item>

        {!!formValues?.hasCustomerAddress && (
          <div>
            {!manualAddress && (
              <>
                <Group position="between" style={{ marginBottom: 16 }}>
                  <Title style={{ margin: 0 }} level={5}>
                    Endereços do destinatário
                  </Title>
                  <Button type="link" onClick={() => setManualAddress(true)}>
                    Novo endereço
                  </Button>
                </Group>

                {!recipientAddresses.length && (
                  <Card style={{ borderRadius: 8 }}>
                    <Empty description="Não há endereço cadastrado para este destinatário, informe um novo endereço" />
                  </Card>
                )}

                {recipientAddresses.map((address) => {
                  const addressComplement = address.complement
                    ? `, ${address.complement}`
                    : '';
                  const addressCity = address.city ? `- ${address.city}` : '';
                  const addressState = address.state
                    ? `- ${address.state}`
                    : '';

                  return (
                    <Card
                      style={{
                        marginBottom: 16,
                        borderRadius: 8,
                        borderColor:
                          addressId === address.id ? '#364068' : undefined,
                      }}
                      bodyStyle={{ padding: 16 }}
                    >
                      <Group>
                        <Space
                          direction="vertical"
                          style={{ width: '100%' }}
                          size={0}
                        >
                          <strong>{applyMask(address.zipCode, cepMask)}</strong>
                          <p
                            style={{ margin: 0 }}
                          >{`${address.street}, ${address.number} ${addressComplement}`}</p>
                          <p
                            style={{ margin: 0 }}
                          >{`${address.district} ${addressCity} ${addressState}`}</p>
                        </Space>

                        <Button
                          icon={<FaCheck size={12} />}
                          size="large"
                          type="primary"
                          ghost={addressId !== address.id}
                          onClick={() => {
                            if (addressId !== address.id) {
                              setAddressId(address.id);
                              applyAddress(address);
                            } else {
                              applyAddress(undefined);
                              setAddressId(undefined);
                            }
                          }}
                        />
                      </Group>
                    </Card>
                  );
                })}
              </>
            )}

            {manualAddress && (
              <>
                <Group position="between" style={{ marginBottom: 16 }}>
                  <Title style={{ margin: 0 }} level={5}>
                    Informar um novo endereço
                  </Title>
                  <Button type="link" onClick={() => setManualAddress(false)}>
                    Selecionar endereço
                  </Button>
                </Group>

                <AddressForm form={addressForm} simpleForm isRequired />
              </>
            )}
          </div>
        )}
      </Space>

      {!!formValues?.hasCustomerAddress && (
        <>
          <Form.Item
            hidden
            noStyle
            name={['recipient', 'deliveryAddress', 'street']}
          />
          <Form.Item
            hidden
            noStyle
            name={['recipient', 'deliveryAddress', 'streetNumber']}
          />
          <Form.Item
            hidden
            noStyle
            name={['recipient', 'deliveryAddress', 'complement']}
          />
          <Form.Item
            hidden
            noStyle
            name={['recipient', 'deliveryAddress', 'district']}
          />
          <Form.Item
            hidden
            noStyle
            name={['recipient', 'deliveryAddress', 'city']}
          />
          <Form.Item
            hidden
            noStyle
            name={['recipient', 'deliveryAddress', 'cityId']}
          />
          <Form.Item
            hidden
            noStyle
            name={['recipient', 'deliveryAddress', 'state']}
          />
          <Form.Item
            hidden
            noStyle
            name={['recipient', 'deliveryAddress', 'zipcode']}
          />
          <Form.Item
            hidden
            noStyle
            name={['recipient', 'deliveryAddress', 'complement']}
          />
        </>
      )}
    </>
  );
};

export default InvoiceCfeRecipientData;
